import { useState } from "react";
import { useLocation } from "react-router-dom";
import { LifeBuoy, X, Send, Phone, Camera } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * HelpMeButton — floating "Help Me, Mike!" button visible in the dashboard.
 * Mike's idea: 60+ users get stuck → click big red button → fill simple form → ticket
 * lands in Mike's Helpdesk + an email confirmation goes to the member.
 */
export default function HelpMeButton() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [phone, setPhone] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const { user } = useAuth();
  const loc = useLocation();

  if (!user) return null;

  const handleScreenshot = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Screenshot is too large (8MB max)."); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setScreenshot(r.data.url);
      toast.success("Screenshot attached.");
    } catch {
      toast.error("Could not attach screenshot.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) { toast.error("Tell Mike what you need help with."); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/help/tickets", {
        subject: subject.trim() || "Need help on " + (loc.pathname || "/"),
        body: body.trim(),
        page_url: window.location.href,
        phone: phone.trim() || null,
        screenshot_url: screenshot || null,
      });
      toast.success(`Ticket ${r.data.short_id} sent to Mike. Check your email for confirmation.`);
      setSubject(""); setBody(""); setPhone(""); setScreenshot(null);
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="help-me-button"
        title="Stuck? Mike will personally reply."
        className="fixed bottom-4 right-4 z-40 bg-[#2E9F3A] hover:bg-[#1F8A2A] text-white w-12 h-12 md:w-auto md:h-auto md:px-4 md:py-2.5 rounded-full shadow-2xl inline-flex items-center justify-center md:gap-2 font-bold uppercase tracking-wider-cap text-xs border-2 border-[#F39A1F] transition hover:scale-105 opacity-90 hover:opacity-100"
      >
        <LifeBuoy className="w-5 h-5"/>
        <span className="hidden md:inline">Help Me!</span>
      </button>

      {open && (
        <div className="fixed inset-0 bg-onyx/70 z-50 flex items-end md:items-center justify-center p-0 md:p-6" onClick={() => setOpen(false)} data-testid="help-modal">
          <div className="bg-white rounded-t-2xl md:rounded-2xl max-w-lg w-full max-h-[92vh] overflow-y-auto shadow-2xl border-t-4 border-[#2E9F3A]" onClick={e=>e.stopPropagation()}>
            <div className="bg-gradient-to-r from-[#0F172A] to-[#2A2521] text-white px-5 py-4 flex items-center justify-between sticky top-0">
              <div>
                <div className="text-[10px] tracking-wider-cap font-bold text-[#F39A1F]">★ HELP ME!</div>
                <div className="font-serif-display text-2xl">Real human reply within 24 hrs</div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="help-modal-close" className="text-white/80 hover:text-white p-1.5 hover:bg-white/10 rounded transition">
                <X className="w-5 h-5"/>
              </button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4" data-testid="help-form">
              <div>
                <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep">Quick subject (optional)</label>
                <input
                  type="text" maxLength={140} value={subject} onChange={e=>setSubject(e.target.value)}
                  placeholder="e.g. Can't add image to my funnel"
                  data-testid="help-subject"
                  className="w-full mt-1 border-2 border-foreground/20 focus:border-[#F39A1F] rounded-md px-3 py-2.5 text-sm bg-white outline-none transition"
                />
              </div>
              <div>
                <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep">What are you stuck on? *</label>
                <textarea
                  rows={5} required value={body} onChange={e=>setBody(e.target.value)}
                  placeholder="Tell me what you were trying to do, and what happened. Plain English is fine — I'm not a robot."
                  data-testid="help-body"
                  className="w-full mt-1 border-2 border-foreground/20 focus:border-[#F39A1F] rounded-md px-3 py-2.5 text-sm bg-white outline-none transition"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep inline-flex items-center gap-1"><Phone className="w-3 h-3"/>Phone (callback, optional)</label>
                  <input
                    type="tel" value={phone} onChange={e=>setPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    data-testid="help-phone"
                    className="w-full mt-1 border-2 border-foreground/20 focus:border-[#F39A1F] rounded-md px-3 py-2.5 text-sm bg-white outline-none transition"
                  />
                </div>
                <div>
                  <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep inline-flex items-center gap-1"><Camera className="w-3 h-3"/>Screenshot (optional)</label>
                  <label className="block mt-1 cursor-pointer border-2 border-dashed border-[#F39A1F]/50 rounded-md px-3 py-2.5 text-xs text-foreground hover:bg-white transition text-center">
                    {screenshot ? "✓ Attached" : "+ Attach image"}
                    <input
                      type="file" accept="image/*" className="hidden"
                      onChange={e => handleScreenshot(e.target.files?.[0])}
                      data-testid="help-screenshot"
                    />
                  </label>
                </div>
              </div>
              <div className="bg-white border-2 border-[#F39A1F]/40 rounded-md p-3 text-[11px] text-foreground">
                <strong>I read every ticket personally.</strong> If I'm slammed, my team handles the basics so I can focus on the deep questions. You'll always get a real reply by email.
              </div>
              <button
                type="submit" disabled={submitting}
                data-testid="help-submit"
                className="w-full bg-[#2E9F3A] hover:bg-[#1F8A2A] disabled:opacity-60 text-white px-5 py-3 rounded-md font-bold uppercase tracking-wider-cap text-sm inline-flex items-center justify-center gap-2 shadow-lg transition"
              >
                <Send className="w-4 h-4"/>{submitting ? "Sending…" : "Send to Mike"}
              </button>
              <div className="text-[10px] tracking-wider-cap font-bold text-foreground/60 text-center">★ FREE FOR EVERY MEMBER · NO BOTS · ONE-CLICK</div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
